// ==========================================
// Types & helpers for WorkflowBuilder
// ==========================================

import type { WorkflowStep as ApiWorkflowStep } from '../../generated/api';

// ==========================================
// Contract Types — re-exported from the generated SDK (server is the
// single source of truth; schema lives in
// server/src/hono/routes/workflow-definition.ts).
// ==========================================

/**
 * Workflow step type used across the builder UI.
 *
 * Base shape comes from the server-generated `WorkflowBuilderStep` (see
 * client/src/generated/api/types.gen.ts, emitted from the Hono
 * schema in server/src/hono/routes/workflow-definition.ts). We
 * extend it with:
 *   - `templateSlug` / `pinnedTools`: join fields the API returns
 *     alongside the raw step row (not on the step table itself).
 *   - UI-only fields: diagram layout + prompt-authoring modes that
 *     live only in the local form state and are never persisted.
 */
export interface WorkflowBuilderStep extends ApiWorkflowStep {
  templateSlug: string | null;
  pinnedTools: string[];
  templateName?: string | null;
  templateVersion?: number | null;
  templateQualityScore?: string | null;
  templateProvenance?: {
    source: string | null;
    sourceRepoUrl: string | null;
    sourceFilePath: string | null;
  } | null;
  // UI-only fields (not persisted)
  diagramPosition?: { x: number; y: number };
  promptMode?: 'system' | 'user' | 'both' | 'freetext' | 'template';
  userPromptTemplate?: string;
  userPromptFreetext?: string;
  userPromptWithVars?: string;
}

export function emptyStep(order: number = 1): WorkflowBuilderStep {
  return {
    id: 0, // temporary, server assigns real ID
    versionId: 0,
    name: '',
    description: null,
    templateId: null,
    templateSlug: null,
    subWorkflowDefinitionId: null,
    executionMode: 'sequential',
    parallelGroup: null,
    order,
    optional: false,
    timeoutMs: null,
    retryMax: null,
    retryBackoffMs: null,
    retryBackoffMultiplier: null,
    systemPromptOverride: null,
    additionalInstructions: null,
    toolSelectionMode: null,
    pinnedTools: [],
    outputConfig: null,
    outputPortType: null,
    // outputContract / inputContracts are optional — left undefined on a fresh step.
    contractNegotiationStatus: null,
    contractVersion: 1,
    conditionField: null,
    conditionOperator: null,
    conditionValue: null,
    approvalStatus: 'pending',
    approvedBy: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

export type ConfigTab = 'config' | 'prompt' | 'output' | 'debug' | 'contract';

/**
 * Compute step labels: sequential steps get 1, 2, 3...
 * Parallel steps in the same group get 3.1, 3.2, etc.
 */
export function computeStepLabels(steps: WorkflowBuilderStep[]): Map<string, string> {
  const labels = new Map<string, string>();
  let seqNum = 0;

  // Group consecutive parallel steps by parallelGroup
  let i = 0;
  while (i < steps.length) {
    const step = steps[i];
    const stepKey = step.id.toString();
    if (step.executionMode === 'parallel' && step.parallelGroup) {
      // Collect all steps in this parallel group
      seqNum++;
      const group = step.parallelGroup;
      let subIdx = 1;
      while (i < steps.length && steps[i].executionMode === 'parallel' && steps[i].parallelGroup === group) {
        labels.set(steps[i].id.toString(), `${seqNum}.${subIdx}`);
        subIdx++;
        i++;
      }
    } else {
      seqNum++;
      labels.set(stepKey, String(seqNum));
      i++;
    }
  }
  return labels;
}

// ==========================================
// Centralized Form State
// ==========================================

export type FlowDirection = 'vertical' | 'horizontal';

/** Canvas-drawn dependency edge between two steps. Stored in form state and persisted via saveGraph. */
export interface WorkflowEdgeForm {
  /** Present once persisted; absent for locally-created edges not yet saved */
  id?: number;
  /** Step id (positive = persisted, negative = temp canvas id) */
  sourceStepId: number;
  /** Step id (positive = persisted, negative = temp canvas id) */
  targetStepId: number;
  /** data = target consumes source output; control = ordering only */
  kind: 'data' | 'control';
  label?: { text: string } | null;
}

export interface WorkflowFormState {
  name: string;
  description: string;
  category: string;
  status: string;
  steps: WorkflowBuilderStep[];
  /** Canvas-drawn dependency edges. Empty = order-inference layout (legacy). */
  edges: WorkflowEdgeForm[];
  tags: string;
  cronSchedule: string;
  scheduleEnabled: boolean;
  flowDirection: FlowDirection;
  /** Server version for optimistic concurrency control */
  version?: number;
}

export const DEFAULT_FORM_STATE: WorkflowFormState = {
  name: '',
  description: '',
  category: 'custom',
  status: 'draft',
  steps: [],
  edges: [],
  tags: '',
  cronSchedule: '',
  scheduleEnabled: false,
  flowDirection: 'vertical',
};

// ==========================================
// Help text constants
// ==========================================

export const HELP = {
  systemPrompt: `The System Prompt defines the agent's identity, expertise, and behavioral rules. It is set automatically based on the Agent Role selected in the Config tab.

You can customize it by clicking "Edit" to override the role default. This is useful when you need the agent to follow specific formatting rules, focus on particular aspects, or adopt a different persona for this step.

The system prompt is always sent as the first message to the AI model and shapes every response the agent produces.`,

  agentInstructionFreetext: `Freetext mode — write the exact instruction sent to the agent.

Benefits:
• Simple and direct — what you write is what the agent gets
• Best for one-off steps that won't be reused with different inputs
• Full control over wording with no abstraction layer
• Easier to read and debug — no placeholder syntax to parse

When to use:
• The step always does the same thing (e.g., "Summarize the portfolio")
• You want to iterate on the prompt quickly without managing variables
• The step receives its dynamic data from a previous step's output, not from parameters

Example:
"Analyze the current US macro environment. Focus on interest rates, inflation trends, and employment data. Provide a regime classification (expansion, contraction, or stagflation) with confidence level."`,

  agentInstructionTemplate: `Template mode — write an instruction with {{variable}} placeholders.

Benefits:
• Reusable — same instruction works with different parameters
• Clone workflows for different markets by only changing Variables
• API/schedule triggers can pass different variables per run
• Separates logic (instruction) from data (variables)

When to use:
• The step runs for different regions, tickers, sectors, or date ranges
• You want to reuse the same workflow across teams/orgs
• The step is triggered programmatically with varying parameters

How it works:
Write {{key}} anywhere in your instruction. Before execution, each {{key}} is replaced with the matching value from the Variables JSON below.

Example instruction:
"Analyze the macro environment for {{regions}}. Focus on: {{focus_areas}}. Use data from the last {{lookback_days}} days."

With variables: {"regions": ["US", "EU"], "focus_areas": ["rates", "inflation"], "lookback_days": 90}

The agent receives:
"Analyze the macro environment for US,EU. Focus on: rates,inflation. Use data from the last 90 days."`,

  variables: `Variables define the dynamic values that get injected into your Template instruction via {{key}} placeholders.

Write valid JSON with key-value pairs. Keys must match the {{key}} names used in your instruction above. Values can be strings, numbers, arrays, or objects.

Example:
{
  "regions": ["US", "EU", "Asia"],
  "focus_areas": ["rates", "inflation", "employment"],
  "lookback_days": 90
}

When the step executes, {{regions}} becomes ["US","EU","Asia"], {{focus_areas}} becomes ["rates","inflation","employment"], etc.`,
};
